/**
 * RAOC-22 PR-2 — Copied from
 * `@atlassian/search-page/src/common/utils/network-task-tracker.ts`.
 *
 * Internal helper for `useRovoConfig`. Not exported via a package subpath.
 *
 * The `task-names`/`vital-stats-events` modules referenced by the search-page
 * version live in search-page. To keep this helper self-contained inside
 * rovo-connectors-config (and free of cross-package cycles), we type the
 * task name as a plain `string` and accept the vital-stats event factories
 * (`onTaskStart` / `onTaskSuccess` / `onTaskFail`) implicitly via the
 * provided `fireAnalyticsEvent` callback — the caller in search-page builds
 * the events at the call site.
 *
 * Brings `fg('rovo-search-vitalstats-tracing-headers')`, registered in this
 * package's `platform-feature-flags` map.
 */

import { fg } from '@atlaskit/platform-feature-flags';
import {
	type NetworkTraceInfo,
	normaliseTraceInfo,
} from '@atlassian/search-client/network-trace-info';
import { type NetworkCallbacks } from '@atlassian/search-client/persist-refresh';

export type NetworkCallbackContext = {
	uxTaskId: string;
	isPreQuery?: boolean;
};

/**
 * Args shared by the three VitalStats event factories.
 */
type VitalStatsEventArgs = {
	taskId: string;
	task: string;
	searchSessionId: string;
	moreContext?: Record<string, unknown>;
};

/**
 * VitalStats event factories owned by this module. The vital-stats event wire
 * shape is part of the `useRovoConfig` contract with the VitalStats taxonomy,
 * not something each caller redefines — owning them here keeps every consumer
 * emitting the same shape and prevents drift between dashboards. Behaviour is
 * identical to search-page's `onTaskStart` / `onTaskSuccess` / `onTaskFail`
 * from `common/utils/events/vital-stats-events.ts`.
 */
const buildVitalStatsEvent = (
	action: 'taskStart' | 'taskSuccess' | 'taskFail',
	{ searchSessionId, taskId, task, moreContext }: VitalStatsEventArgs,
): unknown => ({
	action,
	actionSubject: 'ui',
	eventType: 'operational' as const,
	attributes: {
		additionalAttributes: {
			task,
			taskId,
			hostname: typeof window !== 'undefined' ? window.location.hostname : '',
			searchSessionId,
			...moreContext,
		},
	},
});

export const onTaskStart = (args: VitalStatsEventArgs): unknown =>
	buildVitalStatsEvent('taskStart', args);

export const onTaskSuccess = (args: VitalStatsEventArgs): unknown =>
	buildVitalStatsEvent('taskSuccess', args);

export const onTaskFail = (args: VitalStatsEventArgs): unknown =>
	buildVitalStatsEvent('taskFail', args);

/**
 * Creates network callbacks for tracking network requests with VitalStats analytics.
 *
 * Behaviour is identical to the search-page implementation it was copied from,
 * including the `fg('rovo-search-vitalstats-tracing-headers')` check that gates
 * inclusion of normalised trace headers in `onSuccess` / `onError` events.
 */
/**
 * The vital-stats event payloads built by `VitalStatsEventFactories` are
 * typed as `unknown` at the factory boundary; this helper only ever forwards
 * them straight into the caller-supplied analytics sink. Typing
 * `fireAnalyticsEvent` as `(event: unknown) => void` matches that shape
 * exactly and avoids the unguarded `any` escape hatch. Callers whose own
 * sink is more specific (e.g. `(event: FireAnalyticsEvent) => void`) should
 * pass it as `((event) => fireAnalyticsEvent(event as FireAnalyticsEvent))`
 * or similar — the narrow cast lives at the call site, not in this helper.
 */
export const createVitalStatsNetworkCallbacks = (
	taskId: string,
	taskName: string,
	searchSessionId: string,
	fireAnalyticsEvent: (event: unknown) => void,
	configNetworkTaskStartAt: number,
	context: NetworkCallbackContext,
): NetworkCallbacks => {
	const baseParams = { taskId, task: taskName, searchSessionId };

	return {
		onStart: () => {
			fireAnalyticsEvent(onTaskStart({ ...baseParams, moreContext: context }));
		},
		onSuccess: (traceInfo?: NetworkTraceInfo) => {
			const timeTaken = performance.now() - configNetworkTaskStartAt;
			const tracingHeaders = fg('rovo-search-vitalstats-tracing-headers')
				? normaliseTraceInfo(traceInfo)
				: {};
			fireAnalyticsEvent(
				onTaskSuccess({
					...baseParams,
					moreContext: { ...context, timeTaken, ...tracingHeaders },
				}),
			);
		},
		onError: (errorName: string, errorMessage: string, traceInfo?: NetworkTraceInfo) => {
			const timeTaken = performance.now() - configNetworkTaskStartAt;
			const tracingHeaders = fg('rovo-search-vitalstats-tracing-headers')
				? normaliseTraceInfo(traceInfo)
				: {};
			fireAnalyticsEvent(
				onTaskFail({
					...baseParams,
					moreContext: {
						...context,
						errorName,
						errorMessage,
						timeTaken,
						...tracingHeaders,
					},
				}),
			);
		},
	};
};
